import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import SynchronizationService from "../services/synchronizationService";
import { Toast } from "../components/ui";
import OmieBudgetImportTracker from "../components/synchronizations/budget/OmieBudgetImportTracker";
import { API_BASE_URL, readSseStream, sleep } from "../utils/sse";

const STREAM_EVENTS = ["progress", "completed", "failed"];
const RETRY_DELAYS_MS = [1000, 2000, 5000, 15000, 30000];

/**
 * Acompanha, em qualquer tela da plataforma, as importações de orçamento da Omie em segundo plano.
 * imports: { [syncId]: { jobId, syncId, syncName, ano, mode, returnUrl, status, percentual,
 *                        mesesConcluidos, totalMeses, mensagem, resultado, consumed } }
 * status: "running" | "completed" | "failed". "consumed" indica que o resultado já foi aplicado no grid.
 */
const OmieBudgetImportContext = createContext({
  imports: {},
  startImport: async () => {},
  resumeImport: async () => {},
  consumeImport: () => {},
  dismissImport: () => {},
  registerScreen: () => () => {},
  openScreens: new Set(),
});

const toImportState = (status, meta) => ({
  jobId: status.jobId,
  syncId: status.syncId,
  ano: status.ano,
  status: status.status,
  percentual: status.percentual ?? 0,
  mesesConcluidos: status.mesesConcluidos ?? 0,
  totalMeses: status.totalMeses ?? 12,
  mensagem: status.mensagem ?? null,
  resultado: status.resultado ?? null,
  consumed: false,
  ...meta,
});

export const OmieBudgetImportProvider = ({ children, onNavigate }) => {
  const [imports, setImports] = useState({});
  // syncIds com a tela de orçamento aberta (ela aplica o resultado e mostra o progresso inline)
  const [openScreens, setOpenScreens] = useState(() => new Set());
  const importsRef = useRef(imports);
  importsRef.current = imports;
  const openScreensRef = useRef(openScreens);
  openScreensRef.current = openScreens;

  const updateImport = useCallback((syncId, patch) => {
    setImports((prev) => (prev[syncId] ? { ...prev, [syncId]: { ...prev[syncId], ...patch } } : prev));
  }, []);

  // Se já houver importação em andamento para a sync (ex.: outra aba), o backend devolve a existente;
  // o ano considerado é sempre o do job (status.ano).
  const startImport = useCallback(async ({ syncId, syncName, ano, mode }) => {
    const status = await SynchronizationService.startOmieBudgetImport(syncId, ano);
    if (!status?.jobId) throw new Error("Resposta inválida ao iniciar importação");
    setImports((prev) => ({
      ...prev,
      [syncId]: toImportState(status, {
        syncName,
        mode,
        returnUrl: `${window.location.pathname}${window.location.search}`,
      }),
    }));
    return status;
  }, []);

  // Retoma o acompanhamento de uma importação em andamento (ex.: página recarregada)
  const resumeImport = useCallback(async ({ syncId, syncName }) => {
    if (importsRef.current[syncId]) return;
    try {
      const status = await SynchronizationService.getActiveOmieBudgetImport(syncId);
      if (!status?.jobId || importsRef.current[syncId]) return;
      setImports((prev) => ({
        ...prev,
        [syncId]: toImportState(status, {
          syncName,
          mode: "complete",
          returnUrl: `${window.location.pathname}${window.location.search}`,
        }),
      }));
    } catch {
      // sem importação em andamento ou falha de rede: nada a retomar
    }
  }, []);

  const consumeImport = useCallback((syncId) => updateImport(syncId, { consumed: true }), [updateImport]);

  const dismissImport = useCallback((syncId) => {
    setImports((prev) => {
      if (!prev[syncId]) return prev;
      const next = { ...prev };
      delete next[syncId];
      return next;
    });
  }, []);

  const registerScreen = useCallback((syncId) => {
    setOpenScreens((prev) => new Set(prev).add(syncId));
    return () => {
      setOpenScreens((prev) => {
        const next = new Set(prev);
        next.delete(syncId);
        return next;
      });
    };
  }, []);

  const applyStatus = useCallback((imp, status) => {
    updateImport(imp.syncId, {
      status: status.status,
      percentual: status.percentual ?? 0,
      mesesConcluidos: status.mesesConcluidos ?? 0,
      totalMeses: status.totalMeses ?? 12,
      mensagem: status.mensagem ?? null,
      resultado: status.resultado ?? null,
    });
    const screenOpen = openScreensRef.current.has(imp.syncId);
    if (status.status === "failed") {
      Toast.error(status.mensagem || "Erro ao importar orçamento da Omie");
    } else if (status.status === "completed" && !screenOpen) {
      Toast.success(`Orçamento ${status.ano} da Omie pronto${imp.syncName ? ` para ${imp.syncName}` : ""}. Abra o orçamento para revisar e salvar.`);
    }
  }, [updateImport]);

  // Acompanha a importação por SSE: o servidor envia "progress" a cada mês concluído e encerra o stream
  // com "completed" (com o resultado) ou "failed". Se a conexão cair antes disso, reconecta com backoff
  // (o primeiro evento de cada conexão traz o estado atual, então nada se perde).
  const followImport = useCallback(async (imp, signal) => {
    let attempt = 0;
    while (!signal.aborted) {
      let terminal = false;
      try {
        await readSseStream(`${API_BASE_URL}/orcamentos/importacoes-omie/${imp.jobId}/stream`, {
          signal,
          onEvent: ({ event, data }) => {
            if (!STREAM_EVENTS.includes(event) || !data) return;
            try {
              applyStatus(imp, JSON.parse(data));
            } catch (e) {
              console.error("Evento SSE inválido da importação de orçamento da Omie:", e);
              return;
            }
            attempt = 0;
            if (event !== "progress") terminal = true;
          },
        });
        if (terminal || signal.aborted) return;
      } catch (e) {
        if (signal.aborted) return;
        if (e?.status === 404) {
          updateImport(imp.syncId, { status: "failed", mensagem: "Importação não encontrada ou expirada." });
          Toast.error("Importação de orçamento da Omie não encontrada ou expirada");
          return;
        }
        console.error("Conexão SSE da importação de orçamento da Omie falhou:", e);
      }
      try {
        await sleep(RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)], signal);
      } catch {
        return;
      }
      attempt += 1;
    }
  }, [applyStatus, updateImport]);

  // Uma conexão SSE por importação em andamento (jobId -> AbortController)
  const streamsRef = useRef(new Map());
  const runningJobsKey = useMemo(
    () => Object.values(imports).filter((i) => i.status === "running").map((i) => i.jobId).sort().join(","),
    [imports],
  );

  useEffect(() => {
    const running = Object.values(importsRef.current).filter((i) => i.status === "running");
    const runningIds = new Set(running.map((i) => i.jobId));

    streamsRef.current.forEach((controller, jobId) => {
      if (!runningIds.has(jobId)) {
        controller.abort();
        streamsRef.current.delete(jobId);
      }
    });

    running.forEach((imp) => {
      if (streamsRef.current.has(imp.jobId)) return;
      const controller = new AbortController();
      streamsRef.current.set(imp.jobId, controller);
      followImport(imp, controller.signal).finally(() => {
        if (streamsRef.current.get(imp.jobId) === controller) streamsRef.current.delete(imp.jobId);
      });
    });
  }, [runningJobsKey, followImport]);

  useEffect(() => {
    const streams = streamsRef.current;
    return () => {
      streams.forEach((controller) => controller.abort());
      streams.clear();
    };
  }, []);

  const value = useMemo(() => ({
    imports,
    startImport,
    resumeImport,
    consumeImport,
    dismissImport,
    registerScreen,
    openScreens,
  }), [imports, startImport, resumeImport, consumeImport, dismissImport, registerScreen, openScreens]);

  return (
    <OmieBudgetImportContext.Provider value={value}>
      {children}
      <OmieBudgetImportTracker
        imports={imports}
        hiddenSyncIds={openScreens}
        onDismiss={dismissImport}
        onOpen={(imp) => imp.returnUrl && onNavigate?.(imp.returnUrl)}
      />
    </OmieBudgetImportContext.Provider>
  );
};

export const useOmieBudgetImport = () => useContext(OmieBudgetImportContext);
